import axios, { AxiosError, type AxiosInstance } from 'axios';
import { API_URL, ApiError, ValidationError } from './api';

/**
 * The operator console's HTTP client.
 *
 * A SECOND axios instance, not a base-URL switch on the first. The two
 * principals are physically separate on the server — a tenant token cannot
 * reach /api/platform and a platform token cannot reach /api/v1, both
 * asserted by middleware — and keeping them separate here means no expression
 * in this codebase can evaluate to the wrong token.
 *
 * Derived from the tenant base rather than configured twice: /api/v1 and
 * /api/platform are siblings, and two env vars that must agree is one env var
 * too many.
 */
export const PLATFORM_API_URL = API_URL.replace(/\/v1\/?$/, '/platform');

type Handlers = {
  getToken: () => string | null;
  onUnauthenticated: () => void;
};

let handlers: Handlers | null = null;

export function configurePlatformApi(next: Handlers): void {
  handlers = next;
}

export const platformApi: AxiosInstance = axios.create({
  baseURL: PLATFORM_API_URL,
  headers: { Accept: 'application/json' },
  timeout: 30_000,
});

platformApi.interceptors.request.use((config) => {
  const token = handlers?.getToken();

  if (token) config.headers.Authorization = `Bearer ${token}`;

  // No X-Outlet-Id. An operator has no outlet, and sending one would be a
  // header the server correctly ignores — which is a header that invites
  // somebody to wonder whether it does something.
  return config;
});

platformApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError<Record<string, never>>) => {
    if (!error.response) {
      return Promise.reject(
        new ApiError(0, 'network_error', 'Cannot reach the server. Check your connection.'),
      );
    }

    const { status, data } = error.response;
    const body = (data ?? {}) as Record<string, unknown>;
    const message = typeof body.message === 'string' ? body.message : 'Something went wrong.';

    if (status === 401) {
      /*
       * Clears the PLATFORM session only.
       *
       * The tenant session in the other tab is a different principal and must
       * survive — an operator whose console token expired mid-investigation
       * should not also lose the impersonated workspace they were reading.
       */
      handlers?.onUnauthenticated();

      return Promise.reject(new ApiError(401, 'unauthenticated', message));
    }

    if (status === 422) {
      return Promise.reject(
        new ValidationError(message, (body.errors ?? {}) as Record<string, string[]>),
      );
    }

    // Mapped onto the shared vocabulary rather than inventing a code: one
    // union means an `instanceof ApiError` handler works for either client.
    const code =
      status === 403
        ? 'forbidden'
        : status === 404
          ? 'not_found'
          : status === 429
            ? 'too_many_requests'
            : 'http_error';

    return Promise.reject(new ApiError(status, code, message));
  },
);

export async function platformGet<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const { data } = await platformApi.get<{ data: T }>(url, { params });

  return data.data;
}

/** Paginated endpoints return `meta` alongside `data`. */
export async function platformGetPaginated<T>(
  url: string,
  params?: Record<string, unknown>,
): Promise<{ data: T[]; meta: PlatformPaginationMeta }> {
  const { data } = await platformApi.get<{ data: T[]; meta: PlatformPaginationMeta }>(url, {
    params,
  });

  return data;
}

export async function platformPost<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await platformApi.post<{ data: T }>(url, body);

  return data?.data;
}

export async function platformPut<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await platformApi.put<{ data: T }>(url, body);

  return data.data;
}

/**
 * DELETE, optionally with a body.
 *
 * Axios puts a delete body under `data`, not as the second argument. It is
 * needed for exactly one call — erasing a workspace, which carries a typed
 * confirmation and a reason — and a query string would put a customer's slug
 * and the operator's reason into every access log along the way.
 */
export async function platformDelete<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await platformApi.delete<{ data: T }>(url, { data: body });

  return data?.data;
}

export type PlatformPaginationMeta = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number | null;
  to: number | null;
};
